<template>
  <button
    :class="classes"
    type="button"
    role="checkbox"
    :aria-checked="checked"
    :disabled
    @click="toggle"
  >
    <span :class="$style.control" />

    <span v-if="$slots.default"><slot /></span>
  </button>
</template>

<script lang="ts" generic="T extends boolean | any[]" setup>
import { useCssModule, computed } from 'vue'
import { useCheckbox } from '@/ui-core/composables/use-checkbox'
import type { Theme, Size } from './consts'

defineOptions({
  name: 'UiCheckbox'
})

const props = withDefaults(
  defineProps<{
    modelValue: T
    value?: any
    disabled?: boolean
    theme?: Theme
    size?: Size
  }>(),
  {
    value: undefined,
    disabled: false,
    theme: 'default',
    size: 'default'
  }
)

const emit = defineEmits<{
  'update:modelValue': [modelValue: T]
}>()

defineSlots<{
  default(slotProps: Record<string, never>): any
}>()

const cssModule = useCssModule()

const { checked, toggle } = useCheckbox(props, emit)

const classes = computed(() => [
  cssModule.checkbox,
  cssModule['theme-' + props.theme],
  cssModule['size-' + props.size],
  {
    [cssModule.checked]: checked.value,
    [cssModule.disabled]: props.disabled
  }
])
</script>

<style lang="scss" module>
@use 'theme';
@use 'size';

.checkbox {
  user-select: none;
  display: inline-flex;
  gap: var-default(--checkbox-gap);
  align-items: center;

  /* Size */
  @include size.add(s);

  @include hover-focus {
    .control {
      border-color: var-default(--checkbox-hover-control-border-color);
    }
  }

  &.disabled {
    .control {
      border-color: var-default(--checkbox-disabled-control-border-color);
    }
  }

  &.checked {
    .control {
      border-color: var-default(--checkbox-checked-control-border-color);

      &::before {
        background: var-default(--checkbox-checked-control-mark-bg);
      }
    }

    @include hover-focus {
      .control {
        border-color: var-default(
          --checkbox-checked-hover-control-border-color
        );

        &::before {
          background: var-default(--checkbox-checked-hover-control-mark-bg);
        }
      }
    }

    &.disabled {
      .control {
        border-color: var-default(
          --checkbox-checked-disabled-control-border-color
        );

        &::before {
          background: var-default(--checkbox-checked-disabled-control-mark-bg);
        }
      }
    }
  }

  &:disabled {
    pointer-events: none;
  }
}

.control {
  border-radius: var-default(--checkbox-control-border-radius);
  border: var-default(--checkbox-control-border-width) solid
    var-default(--checkbox-control-border-color);
  width: var-default(--checkbox-control-height);
  flex-shrink: 0;
  height: var-default(--checkbox-control-height);
  transition: border-color var(--root-checkbox-control-transition-duration);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border-radius: var-default(--checkbox-control-mark-border-radius);
    width: var-default(--checkbox-control-mark-height);
    height: var-default(--checkbox-control-mark-height);
    transition: background var(--root-checkbox-control-transition-duration);
  }
}
</style>
